import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@apollo/client';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import hive from '@bombas/hive';

import * as Styled from './NotifyDialogForm.styled';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import { CREATE_PRODUCT_AVAILABILITY_NOTIFICATION } from './NotifyDialog.gql';

const FormSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address.')
    .required('Please enter a valid email address.')
});

/**
 * A form to allow users to sign up for notifications for when an out of stock product is re stocked
 */
const NotifyDialogForm = ({
  sku,
  variantId,
  productId,
  sizes,
  title,
  color,
  image,
  onComplete
}) => {
  const [createNotification, { loading }] = useMutation(
    CREATE_PRODUCT_AVAILABILITY_NOTIFICATION,
    {
      onCompleted: () => {
        hive.track('NOTIFY_ME_SUBSCRIBE');
        onComplete();
      }
    }
  );

  const renderSubtitle = () => (
    <Styled.Subtitle data-testid="NotifyDialogSubtitle">
      In <Styled.TitleCase>{color}</Styled.TitleCase>{' '}
      {sizes.reduce(
        (acc, { name, value }, index) => [
          ...acc,
          <span key={name}>
            {sizes.length > 1 && index === sizes.length - 1 ? ' and ' : ''}
            {name} <Styled.Uppercase>{value}</Styled.Uppercase>
            {index === sizes.length - 1 ? '.' : ' '}
          </span>
        ],
        []
      )}
    </Styled.Subtitle>
  );

  const handleSubmit = async ({ email }, { setErrors }) => {
    try {
      await createNotification({
        variables: {
          email,
          productId: String(productId),
          productVariantId: String(variantId),
          productVariantSku: sku,
          pdpVariantUrl: window.location.href
        }
      });
    } catch (_e) {
      setErrors({ email: 'An error has occurred.' });
    }
  };

  return (
    <>
      <Styled.Title data-testid="NotifyDialogTitle">{title}</Styled.Title>
      {renderSubtitle()}
      <Styled.ImageContainer>
        <ResponsiveImage
          src={image.src}
          alt={image.alt}
          background={convertColorToThemeHexCode('grey40')}
          resolution={{
            small: 240
          }}
        />
      </Styled.ImageContainer>
      <Styled.Blurb>
        Enter your email address and we’ll let you know when this item is back
        in stock.
      </Styled.Blurb>
      <Formik
        initialValues={{ email: '' }}
        validationSchema={FormSchema}
        onSubmit={handleSubmit}
      >
        <Form data-testid="NotifyDialogEmailForm" noValidate>
          <Styled.Email
            id="notifyFormEmail"
            name="email"
            type="email"
            label="Email address"
            data-testid="NotifyDialogEmailInput"
          />
          <Styled.Button
            isFull
            isLoading={loading}
            type="submit"
            variant="primary"
          >
            Notify Me When Available
          </Styled.Button>
        </Form>
      </Formik>
    </>
  );
};

NotifyDialogForm.propTypes = {
  /**
   * A function that is called upon successful notification signup.
   */
  onComplete: PropTypes.func,
  /**
   * Product title
   */
  title: PropTypes.string.isRequired,
  /**
   * Product variant color
   */
  color: PropTypes.string.isRequired,
  /**
   * Size of the product variant. Multiple sizes for multiple products
   */
  sizes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string
    })
  ).isRequired,
  /**
   * Image of the product variant
   */
  image: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string
  }).isRequired,
  /**
   * Product variant sku
   */
  sku: PropTypes.string.isRequired,
  /**
   * id of the product
   */
  productId: PropTypes.number.isRequired,
  /**
   * id of the variant
   */
  variantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired
};

NotifyDialogForm.defaultProps = {
  onComplete: () => {}
};

export default NotifyDialogForm;
